/**
 * @flow
 */

import React, { useCallback } from 'react';
import gql from 'graphql-tag';
import { Loader } from 'semantic-ui-react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { isEmpty, get } from 'lodash';
import { Table, EllipsisLoader } from '../../../components';
import { FormattedMessage } from 'react-intl';
import InfiniteScroll from 'react-infinite-scroller';
import type { Product, ProductEdge, PageInfo } from '../../../type';

type ProductListProps = {
    products: Array<ProductEdge>,
    loadMore: Function,
    pageInfo: PageInfo,
};

// Query for list of products of current supplier
export const GET_PRODUCTS_QUERY = gql`
    query getProducts(
        $first: Int
        $after: String
        $salonFilter: [ID]
        $search: String
    ) {
        viewer {
            id
            ... on Supplier {
                id
                products(
                    first: $first
                    after: $after
                    salonFilter: $salonFilter
                    search: $search
                ) {
                    pageInfo {
                        endCursor
                        hasNextPage
                    }
                    edges {
                        cursor
                        node {
                            id
                            ...product
                        }
                    }
                }
            }
        }
    }

    fragment product on Product {
        name
        salon {
            id
            name
            address
        }
        code
        manufacturer {
            id
            name
        }
        quantity
        price
        supplierPrice
        type
    }
`;

type ProductRowProps = {
    product: Product,
};

const LoadMoreSpinner = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={7} textAlign="center">
            <EllipsisLoader size="1em" />
        </Table.Cell>
    </Table.Row>
);

/**
 * Show this message if inventory list is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={7} textAlign="center">
            <FormattedMessage
                id="pages.inventory.ProductList.noProduct"
                defaultMessage={"You don't have products yet"}
            />
        </Table.Cell>
    </Table.Row>
);

const ProductRow = ({ product }: ProductRowProps) => (
    <Table.Row>
        <Table.Cell>{product.name}</Table.Cell>
        <Table.Cell>{product.salon.name}</Table.Cell>
        <Table.Cell>{product.salon.address}</Table.Cell>
        <Table.Cell>{product.manufacturer.name}</Table.Cell>
        <Table.Cell>{product.code}</Table.Cell>
        <Table.Cell textAlign="right">{product.quantity}</Table.Cell>
        <Table.Cell textAlign="right">
            {parseFloat(product.price).toFixed(2)} €
        </Table.Cell>
    </Table.Row>
);

const ProductList = (props: ProductListProps) => {
    const searchQuery = useSelector(({ inventory }) =>
        inventory.get('searchQuery'),
    );
    const salonFilter = useSelector(({ inventory }) =>
        inventory.get('salonFilter'),
    );

    const { loading, data, fetchMore } = useQuery(GET_PRODUCTS_QUERY, {
        variables: {
            first: 20,
            salonFilter: salonFilter || [],
            search: searchQuery || undefined,
        },
        fetchPolicy: 'cache-and-network',
    });

    const products = get(data, 'viewer.products.edges', []);
    const pageInfo = get(data, 'viewer.products.pageInfo', {});

    const loadMore = useCallback(() => {
        // Skip if we are already loading results or that was the last page
        if (loading || !pageInfo.hasNextPage) {
            return false;
        }

        return fetchMore({
            query: GET_PRODUCTS_QUERY,
            variables: {
                first: 20,
                after: pageInfo.endCursor,
                salonFilter: salonFilter || [],
                search: searchQuery || undefined,
            },
            // Merge current results with new results
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const prevEdges = previousResult.viewer.products.edges;
                const newEdges = fetchMoreResult.viewer.products.edges;

                const newResults = Object.assign({}, fetchMoreResult);
                newResults.viewer.products.edges = [...prevEdges, ...newEdges];

                return newResults;
            },
        });
    }, [loading, pageInfo, fetchMore, salonFilter, searchQuery]);

    // Show spinner while the products list is loading
    if (loading && isEmpty(products)) {
        return <Loader size="big" inline="centered" active />;
    }

    return (
    <Table celled selectable striped fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.name"
                        defaultMessage="Name"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.salon"
                        defaultMessage="Salon"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.salonAddress"
                        defaultMessage="Address"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.manufacturer"
                        defaultMessage="Manufacturer"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.inventory.ProductList.productCode"
                        defaultMessage="Product Code"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.inventory.ProductList.qty"
                        defaultMessage="Quantity"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.inventory.ProductList.price"
                        defaultMessage="Price"
                    />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <InfiniteScroll
            element="tbody"
            pageStart={0}
            loadMore={loadMore}
            initialLoad={false}
            hasMore={pageInfo.hasNextPage}
            loader={<LoadMoreSpinner key={0} />}
            threshold={1}
            useWindow
        >
            {isEmpty(products) ? (
                <EmptyMessage />
            ) : (
                products.map(product => (
                    <ProductRow key={product.cursor} product={product.node} />
                ))
            )}
        </InfiniteScroll>
    </Table>
    );
};

export default ProductList;
